import React, { useEffect, useState, useCallback } from 'react';
import { Container, Box, Button, Dialog, DialogTitle, DialogContent, InputAdornment, IconButton, Typography, TextField, Stack, Avatar, FormControlLabel, Checkbox , Tooltip} from '@mui/material';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import utc from "dayjs/plugin/utc";
import CloseIcon from '@mui/icons-material/Close';
import debounce from "lodash.debounce";
import { searchUserByNumber, addTenant } from '../services/auth'
import { book } from '../services/booking'
import AddUser from './AddUser';
import { useSnackbar } from "../context/snackbar";

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);


const styles = {
    root: {
      margin: 0,
      padding:2,
    },
    closeButton: {
      position: 'absolute',
      right: 1,
      top: 1,
      color: 'palette.grey[500]',
    },
    body:{
        width:'100%',
        display:'flex',
        height: {xs:'60vh', sm:'60vh', md:'100%', lg:'100%'}
      },
    dateCont:{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 10
    },
    dateInd:{
        marginBottom: 20
    },
    tenantCont: {
        marginBottom : 20
    },
    summaryTable: {
      display: 'table',
      borderCollapse: 'separate',
      borderSpacing: '5px',
      mb:2,
    }
  };

const getDefaultTime = (day, x) => {
    if (day && x) {
        let minutes = (x / 100) * 30
        minutes = Math.floor(minutes / 30) * 30;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return dayjs(`${day} ${hours}:${mins}`)
    }
}

const SummarySection = ({timeDiff, rate, discount, setDiscount}) => {
  return (    
    <Box component="fieldset" sx={styles.summaryTable}>
        <legend>Summary</legend>
        <Box sx={{ mb: 1, display: "table-row"}}>
          <Typography sx={{ display: "table-cell" }}>
            Total Hours 
          </Typography>
          <Typography sx={{ display: "table-cell" }}>
            {timeDiff/60} hours
          </Typography>
        </Box>
        <Box sx={{ mb: 1, display: "table-row"}}>
          <Typography sx={{ display: "table-cell" }}>
            Gross Rent 
          </Typography>
          <Typography sx={{ display: "table-cell" }}>
            ₹ {(timeDiff/60) * rate}
          </Typography>
        </Box>
        <Box sx={{ mb: 1, display: "table-row" }}>
          <Typography sx={{ display: 'table-cell', width: '50%', verticalAlign: 'middle' }}>Discount </Typography>
          <TextField
            size="small" 
            sx={{ display: 'table-cell' }}
            value={discount}
            onChange={(e)=>{
              const val = e.target.value
                if ((val <= (timeDiff/60) * rate)) {
                  setDiscount(e.target.value)
                }
            }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              },
            }}
          />
        </Box>
        <Box sx={{ mb: 1, display: "table-row"}}>
          <Typography sx={{ display: "table-cell" }}>
            Nett Rent 
          </Typography>
          <Typography sx={{ display: "table-cell" }}>
            ₹ {((timeDiff/60) * rate) - parseInt(discount || 0, 10)}
          </Typography>
        </Box>
      </Box>
  )
}

const Tenant = ({setSelectedTenant, selectedTenant, setShowAddNew, showAddNew}) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [optionsOpen, setOptionsOpen] = useState(false)

    
    const fetchOptions = async (query) => {
        setLoading(true);
        const opts = await searchUserByNumber(query)
        setOptions(opts);
        setLoading(false);
    };

    const debouncedFetch = useCallback(
        debounce((query) => {
          if (query.length >= 4) {
            setOptionsOpen(true)
            fetchOptions(query);
          } else {
            setOptions([]);
          }
        }, 500), // 500ms debounce delay
        []
    );

    useEffect(() => {
        debouncedFetch(inputValue);
        return () => {
            debouncedFetch.cancel();
        };
    }, [inputValue, debouncedFetch]);
    
    const handleAddUserClick = (e) => {
      setOptionsOpen(false)
      setShowAddNew(true)
      setInputValue("")
    }

    return (
        <div style={styles.tenantCont}>
            <Autocomplete
                disablePortal
                getOptionLabel={option => option.contactNumber}
                id="combo-box-tenant"
                open={optionsOpen}
                options={options}
                disabled={showAddNew}
                onBlur={()=>{setOptionsOpen(false)}}
                onChange={(e, v, r)=>{ setInputValue(""); setOptionsOpen(false); setSelectedTenant(v)}}
                onInputChange={(event, value) => setInputValue(value)}
                noOptionsText={
                  inputValue && inputValue.length > 3 ? (
                    <Button onClick={handleAddUserClick}>Add </Button>
                  ) : (
                    "No options"
                  )
                }
                renderOption={(props, option, { selected }) => {
                  return (
                    <li {...props}>
                      <Stack direction="column" justifyContent="center" alignItems="center" spacing={2}>
                        <Typography>{option.name}</Typography>
                        <Typography variant="caption">{option.contactNumber}</Typography>
                      </Stack>
                    </li>
                  )}}
                sx={{ width: { xs: 250, sm: 300, md:300, lg: 300, xl: 300 } }}
                ListboxProps={{
                    style: {
                        maxHeight:'200px',
                        zIndex:5
                    }
                }}
                renderInput={(params) => {
                  return (<TextField
                    {...params}
                    label="Tenant"
                    placeholder="Type in mobile number"
                    inputProps={{...params.inputProps, value: selectedTenant?.name || params.inputProps.value}}
                />)}}
            />
        </div>
      );
}

const DialogTitleComponent =(props) => {
    const { children, onClose, ...other } = props;
    return (
      <DialogTitle style={{display: 'flex', position: 'relative'}} sx={styles.root} {...other}>
        <Typography >{children}</Typography>
        {onClose ? (
          <IconButton aria-label="close"  style={{right: '25px', position: 'absolute'}} sx={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
    );
  };

  const DateBar = ({fromDate, setFromDate, toDate, setToDate}) => {
    const now = dayjs();
    
    // Helper to calculate min time
    const getMinTime = (date) => {
      if (!date) return null;
  
      // If the selected date is today
      if (date.isSame(now, 'day')) {
        return now.hour() < 8 ? now.set('hour', 7).set('minute', 0) : now.startOf('hour');
      }
      
      // For future dates, set minimum time to 8 AM
      return dayjs().set('hour', 7).set('minute', 0);
    };
  
    //const minTime = getMinTime(fromDate);
    //const minEndTime = getMinTime(toDate);
    //const maxTime = dayjs().set('hour', 22).set('minute', 0); // 10 PM
      return (
          <div style={styles.dateCont}>
              <div style={styles.dateInd} >
                <LocalizationProvider dateAdapter={AdapterDayjs}>                      
                    <DateTimePicker
                        label="From Date"
                        value={fromDate}
                        ampm={true}
                        //timeSteps={{  minutes: 30 }}
                        minutesStep={30}
                        format='DD/MM/YYYY HH:mm'
                        openTo="hours"
                        onChange={(newValue) => {
                            setFromDate(newValue);
                        }}
                      />
                  </LocalizationProvider>
              </div>
              <div style={styles.dateInd}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        label="To Date"
                        value={toDate}
                        ampm={true}
                        openTo="hours"
                        format='DD/MM/YYYY HH:mm'
                        minutesStep={30}
                        onChange={(newValue) => {
                            setToDate(newValue);
                        }}
                      />
                    </LocalizationProvider>
              </div>
        </div>
      )
  }

const AddBooking = (props) => {

    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [selectedTenant, setSelectedTenant] = useState("");
    const [tenants, setTenants] = useState([])
    const [showAddNew, setShowAddNew] = useState(false);
    const [discount, setDiscount] = useState(0)

    const { showSnackbar } = useSnackbar();
    console.log(props.selectedDay, props.x)
    useEffect(()=>{
        if (props.selectedDay && props.x) {
            const _date = getDefaultTime(props.selectedDay, props.x);
            setFromDate(_date)
            setToDate(_date.add(30, 'minute'))
        }
    }, [props.selectedDay, props.x])

    const handleAddBookingClose = () => {
        props.setOpen(false)
    }
    
    const addNewTenant = async (number, name) => {
      try {
        const newTenant = await addTenant(number, name)
        setSelectedTenant(newTenant)
        setShowAddNew(false)
      } catch (e) {
        console.log(e)
      }
    }
    let timeDiff;
    if (toDate && fromDate) {
      timeDiff = toDate.diff(fromDate, 'minute')
    }

    const doBook = async() =>{
      try {
        await book(fromDate.utc().format('YYYY-MM-DD HH:mm:ss'), toDate.subtract(1, 'second').utc().format('YYYY-MM-DD HH:mm:ss'), discount, selectedTenant._id)
        handleAddBookingClose();
        showSnackbar("Booking added successfully!", "success");
        props.setShouldRefresh(true)
      } catch (e) {
        showSnackbar("Could not book", "error");
        console.log(e)
      }
    }
    if (props.open) {
        return (
            <Dialog
                sx={{height:{xs:'90vh', sm:'90vh'}}}
                fullWidth={true}
                maxWidth={"md"}
                aria-labelledby="customized-dialog-title"
                open={props.open}
            >
                <DialogTitleComponent id="customized-dialog-title" onClose={handleAddBookingClose}>
                    Add New Booking
                </DialogTitleComponent>
                <DialogContent sx={{height:{xs:'90vh', sm:'90vh'}}} dividers style={{height: '100%'}}>
                <Container sx={styles.body} style={{flexDirection:'column'}}>
                    <DateBar fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate}/>
                    <Tenant tenants={tenants} selectedTenant={selectedTenant} setSelectedTenant={setSelectedTenant} setShowAddNew={setShowAddNew} showAddNew={showAddNew}/>
                    <AddUser show={showAddNew} setShow={setShowAddNew} addNewTenant={addNewTenant}/>
                    {fromDate && toDate && selectedTenant && (<SummarySection
                      timeDiff={timeDiff}
                      rate={props.rate}
                      discount={discount}
                      setDiscount={setDiscount}
                    />)}
                    <Stack direction="row">
                      <Button sx={{mr: 2}} onClick={handleAddBookingClose} variant='outlined'>Cancel</Button>
                      <Button onClick={doBook} disabled={!(fromDate && toDate && selectedTenant)} variant='contained'>Book</Button>
                    </Stack>
                </Container>
                </DialogContent>
            </Dialog>
        )
    }
    return null;
}


export default AddBooking